import { Language } from '../types';
import { APP_CONFIG } from '../config/settings';

const LOCALE_MAP: Record<Language, string> = {
  en: 'en-US',
  fr: 'fr-FR',
};

/**
 * Formats a date string (YYYY-MM-DD) according to the specified language.
 * Returns format like "January 24" (en) or "24 janvier" (fr)
 */
export function formatDate(dateString: string, lang: Language): string {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString(LOCALE_MAP[lang], {
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Formats a date range with year, e.g., "January 24 - April 1, 2026"
 */
export function formatDateRange(
  startDate: string,
  endDate: string,
  lang: Language
): string {
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  const locale = LOCALE_MAP[lang];

  const startFormatted = start.toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
  });

  const endFormatted = end.toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return `${startFormatted} - ${endFormatted}`;
}

/**
 * Formats a date with year, e.g., "January 24, 2026"
 */
export function formatDateWithYear(dateString: string, lang: Language): string {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString(LOCALE_MAP[lang], {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

// Pre-compute date replacements for each language
const dateReplacements: Record<Language, Record<string, string>> = {
  en: {
    '{dateRange}': formatDateRange(
      APP_CONFIG.START_DATE,
      APP_CONFIG.END_DATE,
      'en'
    ),
    '{startDate}': formatDateWithYear(APP_CONFIG.START_DATE, 'en'),
    '{endDate}': formatDateWithYear(APP_CONFIG.END_DATE, 'en'),
  },
  fr: {
    '{dateRange}': formatDateRange(
      APP_CONFIG.START_DATE,
      APP_CONFIG.END_DATE,
      'fr'
    ),
    '{startDate}': formatDateWithYear(APP_CONFIG.START_DATE, 'fr'),
    '{endDate}': formatDateWithYear(APP_CONFIG.END_DATE, 'fr'),
  },
};

/**
 * Replaces date placeholders in a string with formatted dates
 */
export function replaceDatePlaceholders(text: string, lang: Language): string {
  const replacements = dateReplacements[lang];
  let result = text;
  for (const [placeholder, value] of Object.entries(replacements)) {
    result = result.replace(placeholder, value);
  }
  return result;
}
